import { InputGroup } from "@blueprintjs/core"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { COLUMN_COLORS } from "../models/board"
import styles from "./AddColumn.module.scss"

/**
 * Пунктирная область справа от колонок: заводит новую колонку прямо на доске.
 * Показывается, когда добавление колонок разрешено
 */
export const AddColumn: React.FC<AddColumnProps> = ({ index, empty, onAdd }) => {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState("")
  const [color, setColor] = useState(COLUMN_COLORS[index % COLUMN_COLORS.length])
  const [saving, setSaving] = useState(false)
  const input = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    adding && setTimeout(() => input.current?.focus(), 0)
  }, [adding])

  const cancel = useCallback(() => {
    setAdding(false)
    setName("")
  }, [])

  const submit = useCallback(async () => {
    const value = name.trim()

    if (saving) {
      return
    }

    if (!value) {
      cancel()
      return
    }

    setSaving(true)
    const success = await onAdd(value, color)
    setSaving(false)

    if (success) {
      setName("")
      setAdding(false)
    }
  }, [name, color, saving, onAdd, cancel])

  if (!adding) {
    return (
      <button
        type="button"
        className={styles.placeholder}
        title={empty ? "На доске пока нет ни одной колонки" : undefined}
        onClick={() => setAdding(true)}
      >
        <GeneralIcon type="general-plus-small" size={16} />
        <span className={styles.text}>Добавить колонку</span>
      </button>
    )
  }

  return (
    <div className={styles.form}>
      <div className={styles.colors}>
        {COLUMN_COLORS.map((item) => (
          <button
            type="button"
            key={item}
            className={`${styles.color} ${color === item ? styles.selected : ""}`}
            style={{ backgroundColor: item }}
            onClick={() => setColor(item)}
          />
        ))}
      </div>

      <InputGroup
        inputRef={(el: HTMLInputElement | null) => {
          input.current = el
        }}
        className="planr-default-input"
        value={name}
        placeholder="Название колонки"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            e.preventDefault()
            submit()
          }

          if (e.key === "Escape") {
            cancel()
          }
        }}
      />

      <div className={styles.buttons}>
        <PlanrButton type="greenish" size="small" onClick={submit} disabled={saving || !name.trim()}>
          Добавить
        </PlanrButton>
        <PlanrButton type="graybtn" size="small" onClick={cancel}>
          Отмена
        </PlanrButton>
      </div>
    </div>
  )
}

interface AddColumnProps {
  /** Сколько колонок уже есть: по нему выбираем цвет по умолчанию */
  index: number
  /** На доске нет ни одной колонки — область растягиваем и подсказываем, что делать */
  empty: boolean
  onAdd: (name: string, color: string) => Promise<boolean>
}
